import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
// import { liveRatesService } from '../services/liveRatesService';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PaymentGatewaySelector from '../components/PaymentGatewaySelector';
import SahkarPGQRModal from '../components/SahkarPGQRModal';
// import { formatCurrency } from '../utils/inputValidation';

interface PaymentSummary {
  orderId: string;
  devoteeName: string;
  devoteePhone: string;
  amount: number;
  metalType: string;
  metalQuantity: number;
  ratePerGram: number;
  blockId: string;
  transactionData?: any;
  augmont?: any;
  donation?: any;
}


const DonationPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const API_BASE = process.env.REACT_APP_API_URL || '/api';
  const [currentRates, setCurrentRates] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [otpVerifiedRates, setOtpVerifiedRates] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);

  // Smooth scrolling utility function
  const scrollToElement = (elementId: string, offset: number = 100) => {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100); // Small delay to ensure DOM is updated
  };
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  // Processing/progress states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showProcessing, setShowProcessing] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'verifying' | 'captured' | 'failed'>('idle');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [augmontStatus, setAugmontStatus] = useState<'idle' | 'creating' | 'created' | 'failed'>('idle');
  // New flow states
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [showGatewaySelection, setShowGatewaySelection] = useState(false);
  const [donationFormData, setDonationFormData] = useState<any>(null);
  
  // Customer details states
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [otpSent, setOtpSent] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [otpVerified, setOtpVerified] = useState(false);
  
  // QR Code Modal states
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [qrCodeData, setQrCodeData] = useState<any>(null);

  // Form state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [devoteeName, setDevoteeName] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [devoteePhone, setDevoteePhone] = useState('');
  const [amount, setAmount] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [metalType, setMetalType] = useState<'gold' | 'silver'>('gold');
  const [otp, setOtp] = useState('');
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState<string>('');
  
  // New form states for Suvarna Daan
  const [donationMode, setDonationMode] = useState<'rupee' | 'grams'>('rupee');
  const [donationType, setDonationType] = useState('General Temple Donation');
  const [message, setMessage] = useState('');

  // Fetch current rates
  const fetchCurrentRates = useCallback(async () => {
      setLoading(true);
      setError(null);
    try {
      const response = await axios.get(`${API_BASE}/metal-rates/current`);
      if (response.data.success) {
        setCurrentRates(response.data.data);
      } else {
        setError('Failed to fetch rates');
      }
    } catch (err) {
      setError('Error fetching rates');
      console.error('Error fetching rates:', err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Auto-fetch rates every 30 seconds
  const startAutoFetch = useCallback(() => {
    const interval = setInterval(fetchCurrentRates, 30000);
    return () => clearInterval(interval);
  }, [fetchCurrentRates]);

  // Poll Augmont creation status using orderId (maps to pg_payment_id)
  const pollAugmontStatus = useCallback(async (orderId: string, gatewayId?: string) => {
    console.log('🚀 pollAugmontStatus function called! Order:', orderId, 'Gateway:', gatewayId);
    console.log('🔍 pollAugmontStatus - API_BASE:', API_BASE);
    setAugmontStatus('creating');
    return new Promise<void>((resolve) => {
      const startedAt = Date.now();
      console.log('⏰ Setting up polling interval...');
      const interval = setInterval(async () => {
        console.log('🔄 Polling interval triggered...');
        try {
          // Determine the correct API endpoint based on payment gateway
          // Use passed gatewayId or fallback to selectedPaymentGateway state
          const currentGateway = gatewayId || selectedPaymentGateway;
          let apiEndpoint;
          if (currentGateway === 'sahkar_pg') {
            // For Sahkar PG, use database transaction ID
            apiEndpoint = `${API_BASE}/transactions/${orderId}`;
          } else if (currentGateway === 'razorpay') {
            // For Razorpay, use payment gateway order ID
            apiEndpoint = `${API_BASE}/transactions/payment/${orderId}`;
          } else {
            // Default to database transaction ID for other gateways
            apiEndpoint = `${API_BASE}/transactions/${orderId}`;
          }
          
          console.log('🔍 Polling with gateway:', currentGateway, 'endpoint:', apiEndpoint);
          
          const res = await fetch(`${apiEndpoint}?t=${Date.now()}`, {
            cache: 'no-cache',
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          const json = await res.json();
          
          // Handle both single object and array responses
          let tx;
          if (Array.isArray(json?.data)) {
            tx = json?.data?.[0];
          } else {
            tx = json?.data;
          }
          
          console.log('🔍 Polling Augmont status:', {
            orderId,
            augmont_status: tx?.augmont_status,
            augmont_transaction_id: tx?.augmont_transaction_id,
            status: tx?.status
          });
          
          // Check if Augmont order is created (either by status or transaction_id)
          if (tx?.augmont_transaction_id || tx?.augmont_status === 'created' || tx?.augmont_status === 'completed') {
            setAugmontStatus('created');
            clearInterval(interval);
            setShowProcessing(false);
            console.log('✅ Augmont order detected, stopping polling');
            resolve();
          }
          // timeout after 60s
          if (Date.now() - startedAt > 60000) {
            setAugmontStatus('failed');
            clearInterval(interval);
            setShowProcessing(false);
            console.log('⏰ Augmont polling timeout after 60s');
            resolve();
          }
        } catch (error) {
          console.error('❌ Error polling Augmont status:', error);
        }
      }, 2000);
    });
  }, [API_BASE, selectedPaymentGateway]);

  // Handle payment success
  const handlePaymentSuccess = useCallback(async (orderId: string, gatewayId?: string) => {
    console.log('🎉 handlePaymentSuccess called! Order ID:', orderId, 'Gateway:', gatewayId);
    console.log('🔍 handlePaymentSuccess - API_BASE:', API_BASE);
    
    try {
      // Determine the correct API endpoint based on payment gateway
      // Use passed gatewayId or fallback to selectedPaymentGateway state
      const currentGateway = gatewayId || selectedPaymentGateway;
      let apiEndpoint;
      if (currentGateway === 'sahkar_pg') {
        // For Sahkar PG, use database transaction ID
        apiEndpoint = `${API_BASE}/transactions/${orderId}`;
      } else if (currentGateway === 'razorpay') {
        // For Razorpay, use payment gateway order ID
        apiEndpoint = `${API_BASE}/transactions/payment/${orderId}`;
      } else {
        // Default to database transaction ID for other gateways
        apiEndpoint = `${API_BASE}/transactions/${orderId}`;
      }
      
      console.log('🔍 Using API endpoint:', apiEndpoint, 'for gateway:', currentGateway);
      
      // Fetch actual transaction data from backend
      const response = await fetch(`${apiEndpoint}?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const result = await response.json();
      
      let transactionData = null;
      if (result.success && result.data) {
        // Handle both single object and array responses
        if (Array.isArray(result.data)) {
          transactionData = result.data[0];
        } else {
          transactionData = result.data;
        }
        console.log('📊 Transaction data retrieved:', {
          augmont_transaction_id: transactionData.augmont_transaction_id,
          augmont_status: transactionData.augmont_status,
          status: transactionData.status,
          augmont_invoice_number: transactionData.augmont_invoice_number
        });
      } else {
        console.warn('⚠️ No transaction data found for order ID:', orderId);
        setError('Transaction data not found. Please contact support.');
        return;
      }

      // Update payment summary with actual transaction data
      const summary: PaymentSummary = {
        orderId: transactionData.order_id || orderId,
        devoteeName: transactionData.devotee_name || '',
        devoteePhone: transactionData.devotee_phone_number || '',
        amount: transactionData.amount_inr || 0,
        metalType: transactionData.donation_type || 'gold',
        metalQuantity: transactionData.metal_quantity || 0,
        ratePerGram: transactionData.rate_per_gram || 0,
        blockId: transactionData.block_id || '',
        transactionData: {
          id: transactionData.id,
          order_id: transactionData.order_id || orderId,
          status: transactionData.status,
          amount_inr: transactionData.amount_inr,
          payment_method: transactionData.payment_method,
          pg_payment_id: transactionData.pg_payment_id,
          created_at: transactionData.created_at,
          augmont_transaction_id: transactionData.augmont_transaction_id,
          augmont_status: transactionData.augmont_status,
          augmont_invoice_number: transactionData.augmont_invoice_number
        },
        augmont: (transactionData.augmont_transaction_id || transactionData.augmont_status) ? {
          augmont_order_id: transactionData.augmont_transaction_id,
          status: transactionData.augmont_status || 'pending',
          invoice_number: transactionData.augmont_invoice_number,
          quantity_grams: transactionData.metal_quantity,
          rate_per_gram: transactionData.rate_per_gram,
          block_id: transactionData.block_id
        } : null
      };

      setPaymentSummary(summary);
      setShowPaymentSummary(true);
      setPaymentStatus('success');
      
      // Check if Augmont order is already created
      if (transactionData.augmont_transaction_id || transactionData.augmont_status) {
        setAugmontStatus('created');
        setShowProcessing(false);
        console.log('✅ Augmont order already exists, stopping processing:', {
          augmont_transaction_id: transactionData.augmont_transaction_id,
          augmont_status: transactionData.augmont_status
        });
      } else {
        console.log('⏳ Augmont order not yet created, starting polling...');
        console.log('🔍 Polling details:', { orderId, currentGateway, apiEndpoint });
        console.log('🚀 About to call pollAugmontStatus...');
        setShowProcessing(true);
        setAugmontStatus('creating');
        // Start polling for Augmont order creation
        await pollAugmontStatus(orderId, currentGateway);
        console.log('✅ pollAugmontStatus completed');
      }
      
      // Scroll to payment summary
      setTimeout(() => {
        scrollToElement('payment-summary-section');
      }, 100);
      
    } catch (error) {
      console.error('❌ Error fetching transaction data:', error);
      setError('Failed to fetch transaction details');
    }
  }, [API_BASE, selectedPaymentGateway, pollAugmontStatus]);

  // Fetch rates on mount
  useEffect(() => {
    // Only fetch rates if we're on the donation page
    console.log('🔍 DonationPage useEffect - Current pathname:', location.pathname);
    if (location.pathname === '/users/donation') {
      console.log('✅ On donation page, fetching rates');
      fetchCurrentRates();
      const cleanup = startAutoFetch();
      return cleanup; // Cleanup interval on unmount
      } else {
      console.log('❌ Not on donation page, skipping rate fetch');
    }
  }, [fetchCurrentRates, startAutoFetch, location.pathname]);


  // Early return if not on the correct route (after all hooks are declared)
  if (location.pathname !== '/users/donation') {
    console.log('❌ DonationPage rendered but not on correct route:', location.pathname);
    return null;
  }

  // Handle OTP generation for customer validation
  const handleGenerateOTP = async () => {
    if (!customerPhone || customerPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!customerName || customerName.trim().length < 2) {
      setError('Please enter a valid full name');
      return;
    }

    // Validate name contains only characters
    if (!/^[a-zA-Z\s]+$/.test(customerName.trim())) {
      setError('Name should contain only characters');
      return;
    }

    // Validate phone number format
    if (!/^[6-9]\d{9}$/.test(customerPhone)) {
      setError('Please enter a valid 10-digit mobile number starting with 6-9');
      return;
    }

    try {
      setIsSendingOTP(true);
      setLoading(true);
      const response = await axios.post(`${API_BASE}/user-auth/generate-donation-otp`, {
        mobile_number: customerPhone
      });

      if (response.data.success) {
        setOtpSent(true);
        setOtp(response.data.otp); // Set the OTP from response
        setError(null);
        setShowOTPVerification(true);
        // Scroll to OTP verification section
        scrollToElement('otp-verification-section', 80);
      } else {
        setError(response.data.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error sending OTP');
      console.error('Error sending OTP:', err);
    } finally {
      setIsSendingOTP(false);
      setLoading(false);
    }
  };

  // Handle OTP verification for customer validation
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsVerifyingOTP(true);
      setLoading(true);
      const response = await axios.post(`${API_BASE}/user-auth/verify-donation-otp`, {
        mobile_number: customerPhone,
        otp: otp
      });

      if (response.data.success) {
        setOtpVerified(true);
        setError(null);
        setShowOTPVerification(false);
        setShowGatewaySelection(true);
        
        // Store customer details in donation form data
        setDonationFormData({
          devoteeName: customerName.trim(),
          devoteePhone: customerPhone,
          amount,
          metalType,
          donationMode,
          donationType,
          message,
          currentRates: currentRates
        });
        
        // Scroll to payment gateway selection
        scrollToElement('payment-gateway-section', 80);
      } else {
        setError(response.data.error || 'Invalid OTP');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error verifying OTP');
      console.error('Error verifying OTP:', err);
    } finally {
      setIsVerifyingOTP(false);
      setLoading(false);
    }
  };

  // Dynamically load Razorpay Checkout script if not present
  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (typeof window !== 'undefined' && window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Open Razorpay Checkout modal
  const openRazorpayCheckout = async (
    orderId: string,
    keyId: string,
    amountInr: number,
    transactionId: string
  ) => {
    try {
      console.log('🔍 Opening Razorpay checkout with:', { orderId, keyId, amountInr, transactionId });
      
      // Ensure checkout script is loaded
      const loaded = await loadRazorpayScript();
      if (!loaded || typeof window.Razorpay === 'undefined') {
        console.error('❌ Razorpay script failed to load');
        setError('Failed to load Razorpay Checkout. Please refresh and try again.');
        return;
      }
      if (!keyId) {
        console.error('❌ Missing keyId:', keyId);
        setError('Payment gateway key is missing. Please contact support.');
        return;
      }
      
      console.log('✅ Razorpay script loaded, opening checkout...');

      const options: any = {
        key: keyId,
        amount: Math.round(amountInr * 100),
        currency: 'INR',
        name: 'Temple Cursur',
        description: 'Gold Donation',
        order_id: orderId,
        prefill: {
          name: devoteeName || 'Donor',
          contact: devoteePhone,
          email: 'test@example.com'
        },
        theme: { color: '#f97316' },
        handler: (response: any) => {
          try {
              // Begin processing UI
              setShowProcessing(true);
              setVerifyStatus('verifying');
              setAugmontStatus('idle');
            axios.post(`${API_BASE}/razorpay/verify-payment`, {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
              transaction_id: transactionId
            }).then(async (verifyResp) => {
              if (verifyResp.data?.success) {
                  setVerifyStatus('captured');
                  // If backend already created Augmont order, mark created; else start polling
                  const augOrder = verifyResp.data?.data?.augmont_order;
                  if (augOrder) {
                    setAugmontStatus('created');
                    setShowProcessing(false);
                    await handlePaymentSuccess(orderId, 'razorpay');
                  } else {
                    // Poll transaction for Augmont creation
                    await pollAugmontStatus(orderId, 'razorpay');
                    await handlePaymentSuccess(orderId, 'razorpay');
                  }
              } else {
                  setVerifyStatus('failed');
                setError(verifyResp.data?.error || 'Payment verification failed');
                  setShowProcessing(false);
              }
            }).catch((e) => {
              console.error('Verification error:', e);
              setError('Payment verification error');
                setVerifyStatus('failed');
                setShowProcessing(false);
            });
          } catch (e) {
            console.error('Verification error:', e);
            setError('Payment verification error');
              setVerifyStatus('failed');
              setShowProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setError('Payment cancelled')
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      console.error('Checkout init error:', e);
      setError('Failed to open Razorpay Checkout');
    }
  };


  // Handle donation form submission (first step)
  const handleDonationSubmit = async () => {
    if (amount < 10) {
      setError('Amount must be greater than 10');
      return;
    }

    // Show customer details form for OTP verification
    setShowCustomerDetails(true);
    setError(null);
    setIsPaymentInProgress(false); // Keep form editable until OTP is sent
    
    // Scroll to customer details section
    scrollToElement('customer-details-section', 80);
  };

  // Handle payment gateway selection and proceed with payment
  const handlePaymentGatewaySelect = async (gatewayId: string) => {
    if (!donationFormData) {
      setError('Form data not found. Please try again.');
      return;
    }

    setSelectedPaymentGateway(gatewayId);
    setShowGatewaySelection(false);

    try {
      console.log('🔍 Creating payment with gateway:', gatewayId);
      console.log('📝 Payment data:', {
        amount: donationFormData.amount,
        devoteeName: donationFormData.devoteeName,
        devoteePhone: donationFormData.devoteePhone,
        metalType: donationFormData.metalType
      });
      
      // Use unified payment API with selected gateway
      const response = await axios.post(`${API_BASE}/payments/create-order/${gatewayId}`, {
        user_name: 'Test User',
        user_mobile: donationFormData.devoteePhone,
        user_email: 'test@example.com',
        amount_inr: donationFormData.amount,
        devotee_name: donationFormData.devoteeName,
        devotee_phone: donationFormData.devoteePhone,
        metal_type: donationFormData.metalType,
        quantity_grams: donationFormData.amount / (donationFormData.currentRates?.gold_buy_rate || 10000)
      });

      console.log('📊 Payment creation response:', response.data);
      console.log('📊 Full response object:', response);
      console.log('📊 Response structure check:', {
        success: response.data.success,
        hasOrder: !!response.data.order,
        hasTransaction: !!response.data.transaction,
        hasGateway: !!response.data.gateway,
        hasKeyId: !!response.data.key_id,
        responseKeys: Object.keys(response.data || {}),
        status: response.status
      });

      if (response.data.success) {
        setError(null);

        // Extract data with fallbacks - handle both possible response structures
        const orderId: string = response.data.order?.order_id || response.data.data?.order?.order_id;
        const transactionId: string = response.data.transaction?.id || response.data.data?.transaction?.id;
        const gateway: string = response.data.gateway || response.data.data?.gateway;
        const keyId: string = response.data.key_id || response.data.data?.key_id;

        console.log('🔍 Extracted values:', { orderId, transactionId, gateway, keyId });

        // Check if we got the essential data
        if (!orderId || !transactionId || !gateway) {
          console.error('❌ Missing essential data:', { orderId, transactionId, gateway });
          setError('Invalid response: missing essential payment data');
          return;
        }

        // Store the order ID for summary
        setPaymentSummary(prev => prev ? { ...prev, orderId } : prev);

        // Handle payment based on gateway
        if (gateway === 'razorpay') {
          // Debug: Log the response structure
          console.log('🔍 Razorpay response structure:', {
            key_id: response.data.key_id,
            order_id: orderId,
            amount: donationFormData.amount,
            transaction_id: transactionId
          });
          
          // Open Razorpay Checkout on-screen
          await openRazorpayCheckout(orderId, keyId || '', donationFormData.amount, transactionId);
        } else if (gateway === 'sahkar_pg') {
          // Handle Sahkar PG payment (show QR code modal)
          if (response.data.data.order.payment_link) {
            // Set payment data for QR code modal
            setPaymentData({
              amount_inr: donationFormData.amount,
              devotee_name: donationFormData.devoteeName,
              devotee_phone: donationFormData.devoteePhone,
              metal_type: donationFormData.metalType,
              quantity_grams: donationFormData.amount / (donationFormData.currentRates?.gold_buy_rate || 10000),
              rate_per_gram: donationFormData.currentRates?.gold_buy_rate,
              block_id: donationFormData.blockId
            });
            setQrCodeData(response.data.data);
            setShowQRCodeModal(true);
          } else {
            setError('Payment link not available for Sahkar PG');
          }
        } else {
          setError(`Unsupported payment gateway: ${gateway}`);
        }
      } else {
        setError(response.data.error || 'Failed to create payment');
      }
    } catch (err: any) {
      console.error('❌ Payment creation failed:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(`Error creating payment: ${err.message}`);
    }
  };

  // Start payment status checking
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startPaymentStatusCheck = (orderId: string) => {
    const interval = setInterval(async () => {
      try {
        // Poll Razorpay ORDER status (we only have order_id here)
        const response = await axios.get(`${API_BASE}/razorpay/order-status/${orderId}`);
        if (response.data.success) {
          const orderStatus: string = response.data.data.status; // created | attempted | paid | expired
          if (orderStatus === 'paid') {
            setPaymentStatus('success');
            clearInterval(interval);
            handlePaymentSuccess(orderId, 'razorpay');
          } else if (orderStatus === 'expired') {
            setPaymentStatus('failed');
            clearInterval(interval);
            setError('Payment expired');
          } else {
            setPaymentStatus('pending');
          }
        }
      } catch (err) {
        console.error('Error checking order status:', err);
      }
    }, 3000);

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(interval);
      if (paymentStatus === 'pending') {
        setPaymentStatus('failed');
        setError('Payment timeout');
      }
    }, 300000);
  };



  // Copy to clipboard function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard');
    });
  };

  // Format time function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Navigation handlers
  const handleBackToMain = () => {
    setShowPaymentSummary(false);
    setShowGatewaySelection(false);
    setShowOTPVerification(false);
    setShowCustomerDetails(false);
    setPaymentSummary(null);
    setDonationFormData(null);
    setPaymentStatus('pending');
    setTimeLeft(300);
    setIsPaymentInProgress(false);
    
    // Reset customer details
    setCustomerName('');
    setCustomerPhone('');
    setIsSendingOTP(false);
    setIsVerifyingOTP(false);
    setOtpVerified(false);
    setOtpSent(false);
    setOtp('');
    setSelectedPaymentGateway('');
    setError(null);
    
    // Scroll back to donation form
    scrollToElement('donation-form-section', 80);
  };

  const handleBackToDashboard = () => {
    window.location.href = '/dashboard';
  };

  // Calculate metal quantity based on amount
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAmountChange = (newAmount: number) => {
    setAmount(newAmount);
  };

  return (
    <>
      <div className="min-h-screen bg-black/20 backdrop-blur-sm">
          {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
            <LanguageSwitcher />
          </div>
          
          {/* Navigation */}
        <div className="flex justify-between items-center p-4">
          <button
            onClick={handleBackToMain}
            className="text-white hover:text-gray-200 transition-colors"
          >
              ← {t('common.back')} to {t('donation.title')}
          </button>
          
          <button
            onClick={handleBackToDashboard}
            className="text-white hover:text-gray-200 transition-colors"
          >
            ← {t('common.back')} to {t('navigation.home')}
          </button>
        </div>

        <div className="container mx-auto px-4 py-6 space-y-6">
          
          {/* Top Card - Live Gold Rates */}
          <div className="bg-white rounded-3xl shadow-xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-2">
                <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
                </svg>
                <h2 className="text-xl font-bold text-gray-800">Live Gold Rates</h2>
                  </div>
                  <button
                    onClick={fetchCurrentRates}
                    disabled={loading}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                )}
                  </button>
              </div>

              {loading && (
                <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                <p className="text-gray-600">Loading rates...</p>
                </div>
              )}

              {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

                {(currentRates || otpVerifiedRates) && (
                <div className="space-y-4">
                {/* Buy Rate Display */}
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-2">Buy Rate (999 Purity)</p>
                  <p className="text-3xl font-bold text-gray-900">
                    ₹{((otpVerifiedRates || currentRates)!.gold_buy_rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-gray-500 text-sm">per gram</p>
                    </div>

                {/* Source and Update Info */}
                <div className="flex justify-between items-center text-sm text-gray-600">
                      <div>
                    <span className="font-medium">Source:</span> Augmont
                      </div>
                      <div>
                    <span className="font-medium">Updated:</span> {new Date().toLocaleTimeString('en-IN', { hour12: false })}
                      </div>
                    </div>
              </div>
                        )}
                    </div>

          {/* Bottom Card - Suvarna Daan Form */}
          <div id="donation-form-section" className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Orange Gradient Header */}
            <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-6">
              <div className="flex items-center space-x-4">
                {/* Temple Logo */}
                <div className="w-16 h-16 rounded-full flex items-center justify-center">
                  <img 
                    src="/images/Asset2.png" 
                    alt="Temple" 
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
                
                {/* Title */}
                <div>
                  <h1 className="text-2xl font-bold text-white">{t('donation.title')}</h1>
                  <p className="text-white/90 text-sm">{t('temple.matangDevsthan')} • {t('temple.jejuri')}</p>
                </div>
            </div>
          </div>

            {/* White Form Section */}
            <div className="p-6 space-y-6">
              
              {/* Donation Mode Selection */}
                  <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Donation Mode</label>
                <div className="flex space-x-3">
                      <button
                    onClick={() => !isPaymentInProgress && setDonationMode('rupee')}
                    disabled={isPaymentInProgress}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border-2 transition-colors ${
                      isPaymentInProgress 
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : donationMode === 'rupee'
                        ? 'bg-orange-50 border-orange-500 text-orange-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span className="font-medium">{t('donation.byRupeeAmount')}</span>
                      </button>
                        <button
                    onClick={() => !isPaymentInProgress && setDonationMode('grams')}
                    disabled={isPaymentInProgress}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border-2 transition-colors ${
                      isPaymentInProgress 
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : donationMode === 'grams'
                        ? 'bg-orange-50 border-orange-500 text-orange-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span className="font-medium">{t('donation.byGoldGrams')}</span>
                        </button>
                </div>
                    </div>

              {/* Quick Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Quick Amount Selection</label>
                <div className="grid grid-cols-3 gap-3">
                  {[500, 1000, 2500, 5000, 10000, 25000].map((quickAmount) => (
                        <button
                      key={quickAmount}
                      onClick={() => !isPaymentInProgress && setAmount(quickAmount)}
                      disabled={isPaymentInProgress}
                      className={`py-3 px-4 rounded-xl border-2 transition-colors ${
                        isPaymentInProgress 
                          ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                          : amount === quickAmount
                          ? 'bg-orange-50 border-orange-500 text-orange-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <span className="font-medium">₹{quickAmount.toLocaleString()}</span>
                        </button>
                  ))}
                      </div>
                  </div>

              {/* Amount Input */}
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {donationMode === 'rupee' ? 'Rupee Amount' : 'Gold Grams'}
                </label>
                <div className="flex">
                  <div className={`flex-shrink-0 px-4 py-3 border border-r-0 rounded-l-xl font-medium ${
                    isPaymentInProgress 
                      ? 'bg-gray-100 border-gray-200 text-gray-400'
                      : 'bg-gray-50 border-gray-300 text-gray-700'
                  }`}>
                    {donationMode === 'rupee' ? '₹' : 'g'}
                  </div>
                    <input
                        type="number"
                        value={amount}
                    onChange={(e) => !isPaymentInProgress && setAmount(Number(e.target.value))}
                    placeholder={donationMode === 'rupee' ? 'Enter amount in rupees' : 'Enter grams'}
                    min={donationMode === 'rupee' ? '10.01' : '0'}
                    step={donationMode === 'rupee' ? '1' : '0.01'}
                    disabled={isPaymentInProgress}
                    className={`flex-1 px-4 py-3 border rounded-r-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                      isPaymentInProgress 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-900'
                    }`}
                          />
                        </div>
                  </div>

              {/* Donation Type */}
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Donation Type</label>
                      <select
                  value={donationType}
                  onChange={(e) => !isPaymentInProgress && setDonationType(e.target.value)}
                  disabled={isPaymentInProgress}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    isPaymentInProgress 
                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="General Temple Donation">General Temple Donation</option>
                  <option value="Special Puja">Special Puja</option>
                  <option value="Temple Maintenance">Temple Maintenance</option>
                  <option value="Festival Celebration">Festival Celebration</option>
                  <option value="Other">Other</option>
                      </select>
                    </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message (Optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => !isPaymentInProgress && setMessage(e.target.value)}
                  placeholder="Add a personal message or prayer request..."
                  rows={3}
                  disabled={isPaymentInProgress}
                  className={`w-full px-4 py-3 border rounded-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none ${
                    isPaymentInProgress 
                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-900'
                  }`}
                />
                          </div>

              {/* Donate Button */}
                        <button
                      onClick={handleDonationSubmit}
                      disabled={isPaymentInProgress}
                className={`w-full font-bold py-4 px-6 rounded-xl transition duration-200 shadow-lg flex items-center justify-center space-x-2 ${
                  isPaymentInProgress 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white'
                }`}
                    >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>{isPaymentInProgress ? t('donation.paymentInProgress') : `${t('donation.donate')} ₹${amount.toLocaleString()}`}</span>
                        </button>
                          </div>
                          </div>


          {/* Customer Details Step */}
          {showCustomerDetails && (
            <div id="customer-details-section" className="bg-white rounded-3xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Customer Verification</h2>
              
              {/* Donation Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h3 className="text-blue-800 font-semibold mb-3 flex items-center">
                  <span className="mr-2">📋</span>
                  Donation Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-600">Amount</p>
                    <p className="text-blue-800 font-bold">₹{amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Donation Type</p>
                    <p className="text-blue-800 font-semibold">{donationType}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Quantity</p>
                    <p className="text-blue-800 font-semibold">{(amount / (currentRates?.gold_buy_rate || 10000)).toFixed(4)} g</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Rate per Gram</p>
                    <p className="text-blue-800">₹{currentRates?.gold_buy_rate || 'Loading...'}</p>
                  </div>
                </div>
              </div>

              {/* Customer Details Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter your full name"
                    disabled={isSendingOTP}
                    className={`w-full px-4 py-3 border rounded-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                      isSendingOTP 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Only characters and spaces allowed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter 10-digit mobile number"
                    disabled={isSendingOTP}
                    className={`w-full px-4 py-3 border rounded-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                      isSendingOTP 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">10-digit mobile number starting with 6-9</p>
                </div>

                <button
                  onClick={handleGenerateOTP}
                  disabled={isSendingOTP || !customerName.trim() || customerPhone.length !== 10}
                  className={`w-full font-bold py-4 px-6 rounded-xl transition duration-200 shadow-lg flex items-center justify-center space-x-2 ${
                    isSendingOTP || !customerName.trim() || customerPhone.length !== 10
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>{isSendingOTP ? 'Sending OTP...' : 'Send OTP'}</span>
                </button>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => {
                    setShowCustomerDetails(false);
                    setIsPaymentInProgress(false);
                    setCustomerName('');
                    setCustomerPhone('');
                    setError(null);
                    scrollToElement('donation-form-section', 80);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition duration-200"
                >
                  Back to Form
                </button>
              </div>
            </div>
          )}

          {/* OTP Verification Step */}
          {showOTPVerification && (
            <div id="otp-verification-section" className="bg-white rounded-3xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Verify Mobile Number</h2>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">OTP Sent Successfully!</h3>
                <p className="text-gray-600">
                  We've sent a 6-digit OTP to <span className="font-semibold text-gray-800">{customerPhone}</span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    disabled={isVerifyingOTP}
                    className={`w-full px-4 py-3 border rounded-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center text-2xl tracking-widest ${
                      isVerifyingOTP 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                <button
                  onClick={handleVerifyOTP}
                  disabled={isVerifyingOTP || otp.length !== 6}
                  className={`w-full font-bold py-4 px-6 rounded-xl transition duration-200 shadow-lg flex items-center justify-center space-x-2 ${
                    isVerifyingOTP || otp.length !== 6
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>{isVerifyingOTP ? 'Verifying...' : 'Verify OTP'}</span>
                </button>

                <div className="text-center">
                  <button
                    onClick={handleGenerateOTP}
                    disabled={isSendingOTP}
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium underline disabled:text-gray-400"
                  >
                    {isSendingOTP ? 'Sending...' : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => {
                    setShowOTPVerification(false);
                    setOtp('');
                    setOtpSent(false);
                    setError(null);
                    scrollToElement('customer-details-section', 80);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition duration-200"
                >
                  Back to Details
                </button>
              </div>
            </div>
          )}

          {/* Payment Gateway Selection Step */}
          {showGatewaySelection && donationFormData && (
            <div id="payment-gateway-section" className="bg-white rounded-3xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Choose Payment Method</h2>
                
              {/* Donation Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h3 className="text-blue-800 font-semibold mb-3 flex items-center">
                  <span className="mr-2">📋</span>
                  Donation Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-600">Devotee Name</p>
                    <p className="text-blue-800 font-semibold">{donationFormData.devoteeName}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Phone Number</p>
                    <p className="text-blue-800 font-semibold">{donationFormData.devoteePhone}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Amount</p>
                    <p className="text-blue-800 font-bold">₹{donationFormData.amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Donation Type</p>
                    <p className="text-blue-800 font-semibold">{donationFormData.donationType}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Quantity</p>
                    <p className="text-blue-800 font-semibold">{(donationFormData.amount / donationFormData.currentRates?.gold_buy_rate).toFixed(4)} g</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Rate per Gram</p>
                    <p className="text-blue-800">₹{donationFormData.currentRates?.gold_buy_rate}</p>
                  </div>
                </div>
              </div>

                  <PaymentGatewaySelector
                    onGatewaySelect={setSelectedPaymentGateway}
                    selectedGateway={selectedPaymentGateway}
                    disabled={!donationFormData.devoteeName || !donationFormData.devoteePhone || !donationFormData.amount}
                  />

                  {selectedPaymentGateway && (
                    <div className="mt-4">
                      <button
                        onClick={() => handlePaymentGatewaySelect(selectedPaymentGateway)}
                        disabled={!donationFormData.devoteeName || !donationFormData.devoteePhone || !donationFormData.amount}
                        className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        Proceed with Payment
                      </button>
                    </div>
                  )}

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => {
                    setShowGatewaySelection(false);
                    setShowOTPVerification(true);
                    setOtp('');
                    setError(null);
                    // Scroll back to OTP verification
                    scrollToElement('otp-verification-section', 80);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition duration-200"
                >
                  Back to OTP
                </button>
              </div>
              </div>
            )}

              {/* Payment Summary Display */}
              {showPaymentSummary && paymentSummary && (
            <div id="payment-summary-section" className="bg-white rounded-3xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">🎉 Payment Successful!</h2>
                  
                  <div className="space-y-6">
                    {/* Success Message */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <div className="text-4xl mb-2">🎉</div>
                  <h3 className="text-green-800 font-bold text-lg mb-2">Digital Gold Donation Successful!</h3>
                  <p className="text-green-700 text-sm">Your donation has been processed successfully.</p>
                    </div>

                {/* Payment Gateway Details */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="text-gray-800 font-semibold mb-3 flex items-center">
                        <span className="mr-2">💳</span>
                    Payment Gateway Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                      <p className="text-gray-600 font-medium">Order ID</p>
                      <p className="text-gray-900 font-mono">{paymentSummary.orderId}</p>
                        </div>
                        <div>
                      <p className="text-gray-600 font-medium">Payment Status</p>
                      <p className="text-gray-900 font-semibold">
                        {paymentSummary.transactionData?.razorpay_status === 'captured' ? '✅ Captured' : 
                         paymentSummary.transactionData?.status === 'completed' ? '✅ Completed' : '⏳ Processing'}
                      </p>
                        </div>
                        <div>
                      <p className="text-gray-600 font-medium">Amount Paid</p>
                      <p className="text-gray-900 font-bold">₹{paymentSummary.amount.toFixed(2)}</p>
                        </div>
                        <div>
                      <p className="text-gray-600 font-medium">Payment Method</p>
                      <p className="text-gray-900">
                        {paymentSummary.transactionData?.payment_method === 'razorpay' ? 'Razorpay' : 
                         'Digital Payment'}
                      </p>
                        </div>
                    {paymentSummary.transactionData?.pg_payment_id && (
                      <div>
                        <p className="text-gray-600 font-medium">Payment Gateway ID</p>
                        <p className="text-gray-900 font-mono text-xs">{paymentSummary.transactionData.pg_payment_id}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-600 font-medium">Transaction Date</p>
                      <p className="text-gray-900">
                        {paymentSummary.transactionData?.created_at ? 
                          new Date(paymentSummary.transactionData.created_at).toLocaleString() : 
                          new Date().toLocaleString()}
                      </p>
                    </div>
                      </div>
                  </div>

                {/* Augmont Processing Status */}
                {(() => {
                  const showProcessing = !paymentSummary.transactionData?.augmont_transaction_id && 
                                       !paymentSummary.transactionData?.augmont_status &&
                                       paymentSummary.transactionData?.status === 'completed';
                  console.log('🔍 Processing spinner condition check:', {
                    hasAugmontTransactionId: !!paymentSummary.transactionData?.augmont_transaction_id,
                    hasAugmontStatus: !!paymentSummary.transactionData?.augmont_status,
                    status: paymentSummary.transactionData?.status,
                    showProcessing
                  });
                  return showProcessing;
                })() && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <h3 className="text-yellow-800 font-semibold mb-3 flex items-center">
                      <span className="mr-2">⏳</span>
                      Processing Digital Gold Allocation
                    </h3>
                    <div className="text-sm">
                      <p className="text-yellow-700 mb-2">
                        Your payment has been received successfully. We are now processing your digital gold allocation with Augmont.
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                        <span className="text-yellow-600">Creating Augmont order...</span>
                      </div>
                      <button
                        onClick={() => {
                          console.log('🔄 Manual refresh triggered');
                          if (paymentSummary?.orderId) {
                            handlePaymentSuccess(paymentSummary.orderId, selectedPaymentGateway);
                          }
                        }}
                        className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs"
                      >
                        Refresh Status
                      </button>
                    </div>
                  </div>
                )}

                {/* Augmont Transaction Details */}
                    {(() => {
                      const showAugmontDetails = (paymentSummary.transactionData?.augmont_transaction_id || 
                                                paymentSummary.transactionData?.augmont_status);
                      console.log('🔍 Augmont details condition check:', {
                        hasAugmontTransactionId: !!paymentSummary.transactionData?.augmont_transaction_id,
                        hasAugmontStatus: !!paymentSummary.transactionData?.augmont_status,
                        showAugmontDetails
                      });
                      return showAugmontDetails;
                    })() && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                    <h3 className="text-gray-800 font-semibold mb-3 flex items-center">
                          <span className="mr-2">🏦</span>
                      Augmont Digital Gold Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                        <p className="text-gray-600 font-medium">Augmont Order ID</p>
                        <p className="text-gray-900 font-mono">{paymentSummary.transactionData.augmont_transaction_id}</p>
                          </div>
                          <div>
                        <p className="text-gray-600 font-medium">Order Status</p>
                        <p className="text-gray-900 font-semibold">
                          {paymentSummary.transactionData.augmont_status === 'completed' ? '✅ Completed' : 
                           paymentSummary.transactionData.augmont_status === 'processing' ? '⏳ Processing' : '⏳ Pending'}
                        </p>
                          </div>
                          <div>
                        <p className="text-gray-600 font-medium">Invoice Number</p>
                        <p className="text-gray-900 font-mono">{paymentSummary.transactionData.augmont_invoice_number || 'Generating...'}</p>
                          </div>
                          <div>
                        <p className="text-gray-600 font-medium">Gold Quantity</p>
                        <p className="text-gray-900 font-bold">{paymentSummary.metalQuantity.toFixed(6)} g</p>
                          </div>
                          <div>
                        <p className="text-gray-600 font-medium">Rate per Gram</p>
                        <p className="text-gray-900">₹{paymentSummary.ratePerGram.toFixed(2)}</p>
                          </div>
                          <div>
                        <p className="text-gray-600 font-medium">Block ID</p>
                        <p className="text-gray-900 font-mono">{paymentSummary.blockId}</p>
                          </div>
                        </div>
                    </div>
                  )}

                {/* Donation Details */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="text-gray-800 font-semibold mb-3 flex items-center">
                    <img 
                      src="/images/Asset2.png" 
                      alt="Temple" 
                      className="w-5 h-5 mr-2 object-contain"
                    />
                    Donation Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">Devotee Name</p>
                      <p className="text-gray-900 font-semibold">{paymentSummary.devoteeName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Phone Number</p>
                      <p className="text-gray-900">{paymentSummary.devoteePhone}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Metal Type</p>
                      <p className="text-gray-900 capitalize">{paymentSummary.metalType}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Donation Amount</p>
                      <p className="text-gray-900 font-bold">₹{paymentSummary.amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                    {/* Temple Information */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="text-gray-800 font-semibold mb-3 flex items-center">
                        <img 
                          src="/images/Asset2.png" 
                          alt="Temple" 
                          className="w-5 h-5 mr-2 object-contain"
                        />
                        Temple Information
                      </h3>
                      <div className="text-sm">
                    <p className="text-gray-800 mb-1 font-semibold">श्री मार्तंड देवसंस्थान (Mandir Setu)</p>
                    <p className="text-gray-700 mb-1">Temple Address, City, State, PIN</p>
                    <p className="text-gray-700">Contact: +91 XXXXXXXXXX</p>
                      </div>
                  </div>

                    {/* Next Steps */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h3 className="text-green-800 font-semibold mb-3 flex items-center">
                        <span className="mr-2">📧</span>
                        What's Next?
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start space-x-2">
                      <span className="text-green-600">1.</span>
                      <span className="text-green-700">Your digital gold has been allocated to your Augmont account</span>
                        </div>
                        <div className="flex items-start space-x-2">
                      <span className="text-green-600">2.</span>
                      <span className="text-green-700">You will receive a confirmation SMS on your registered mobile number</span>
                        </div>
                        <div className="flex items-start space-x-2">
                      <span className="text-green-600">3.</span>
                      <span className="text-green-700">A detailed receipt will be sent to your email (if provided)</span>
                        </div>
                        <div className="flex items-start space-x-2">
                      <span className="text-green-600">4.</span>
                      <span className="text-green-700">You can track your digital gold in your Augmont dashboard</span>
                        </div>
                        <div className="flex items-start space-x-2">
                      <span className="text-green-600">5.</span>
                      <span className="text-green-700">Your donation will be used for temple development and maintenance</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                  <button
                        onClick={handleBackToMain}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition duration-200"
                  >
                        Make Another Donation
                  </button>
                      <button
                        onClick={handleBackToDashboard}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition duration-200"
                      >
                        Go to Dashboard
                      </button>
                    </div>

                    {/* Print/Download Option */}
                    <div className="text-center">
                  <button
                        onClick={() => window.print()}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                        🖨️ Print Receipt
                  </button>
                    </div>
                  </div>
                </div>
              )}

      {/* Sahkar PG QR Code Modal */}
      {showQRCodeModal && paymentData && qrCodeData && (
        <SahkarPGQRModal
          isOpen={showQRCodeModal}
          onClose={() => {
            setShowQRCodeModal(false);
            setPaymentData(null);
            setQrCodeData(null);
            // Reset form and go back to donation flow
            setSelectedPaymentGateway('');
            setShowGatewaySelection(false);
            setDonationFormData(null);
            setError(null);
            setPaymentStatus('pending');
            // Scroll back to donation form
            scrollToElement('donation-form-section', 80);
          }}
          paymentData={paymentData}
          qrCodeData={qrCodeData}
          onPaymentSuccess={(orderId) => {
            console.log('✅ QR Modal Payment successful:', orderId);
            console.log('🔍 About to call handlePaymentSuccess with gateway: sahkar_pg');
            setShowQRCodeModal(false);
            setPaymentData(null);
            setQrCodeData(null);
            // Handle payment success
            handlePaymentSuccess(orderId, 'sahkar_pg');
          }}
          onPaymentFailure={(error) => {
            console.log('❌ Payment failed:', error);
            setError(error);
            setShowQRCodeModal(false);
            setPaymentData(null);
            setQrCodeData(null);
            // Reset form and go back to donation flow
            setSelectedPaymentGateway('');
            setShowGatewaySelection(false);
            setDonationFormData(null);
            setPaymentStatus('pending');
            // Scroll back to donation form
            scrollToElement('donation-form-section', 80);
          }}
        />
      )}
      </div>
    </div>
    </>
  );
};

export default DonationPage; 